import { useMediaQuery } from '@mui/material';
import React from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'store';
import ToolsPanelChrome from './tools-panel/ToolsPanelChrome';

interface RightAppSidebarContentProps {
  children: React.ReactNode;
  id: string;
}

const RightAppSidebarContent: React.FC<RightAppSidebarContentProps> = ({ children, id }) => {
  const activeId = useSelector((state) => state.dualSidebar.right.activeId);
  const chatPanelStyle = useSelector((state) => state.dualSidebar.right.chatPanelStyle);
  const rightOpen = useSelector((state) => state.dualSidebar.right.open);
  const downMidXl = useMediaQuery((theme) => theme.breakpoints.down('midxl'));

  // Force re-render when downMidXl changes
  React.useEffect(() => {
    // This effect ensures the component re-renders when downMidXl changes
  }, [downMidXl]);

  const isActive = activeId === id;

  /** אחרי commit — getElementById בשלב render עלול להיכשל לפני שמיכל היעד קיים, ואז התוכן יופיע רק אחרי resize */
  const [portalMountNode, setPortalMountNode] = React.useState<HTMLElement | null>(null);
  React.useLayoutEffect(() => {
    if (!isActive || !rightOpen) {
      setPortalMountNode(null);
      return;
    }

    const resolveMountNode = () => document.getElementById('right-sidebar-content-wrapper');
    const immediate = resolveMountNode();
    if (immediate) {
      setPortalMountNode(immediate);
      return;
    }

    // Drawer persistent/temporary — מיכל הפורטל עלול להופיע frame אחד אחרי open
    let cancelled = false;
    const rafId = requestAnimationFrame(() => {
      if (cancelled) return;
      const deferred = resolveMountNode();
      if (deferred) setPortalMountNode(deferred);
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
    };
  }, [isActive, rightOpen, activeId, id]);

  if (!isActive || !portalMountNode) return null;

  const inner =
    chatPanelStyle === true ? (
      <ToolsPanelChrome panelId={id} isActive={isActive}>
        {children}
      </ToolsPanelChrome>
    ) : (
      children
    );

  return createPortal(inner, portalMountNode);
};

export default React.memo(RightAppSidebarContent);
